'use client';

import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { ExternalLinkSystemIcon } from '@nib/icons';
import { Hidden } from '@nib/layout';
import { linkLoud, linkDestructive, standardFocusStyleDeclarations, token } from '@nib-components/theme';
export const validColorValues = ['loud', 'dark', 'light', 'destructive'];
export const validIconPlacementValues = ['left', 'right', 'start', 'end'];
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const IconAlignment = styled(_ref => {
  let {
    iconPlacement,
    ...props
  } = _ref;
  return /*#__PURE__*/_jsx("span", {
    ...props
  });
})`
  vertical-align: middle;
  line-height: 1;

  /* Match icon size to font size */

  > svg {
    width: 1em;
    height: 1em;
  }

  ${_ref2 => {
  let {
    iconPlacement
  } = _ref2;
  return iconPlacement === 'left' || iconPlacement === 'start' ? css`
          margin-inline-end: 0.5rem;
        ` : css`
          margin-inline-start: 0.5rem;
        `;
}};
`;
export const BaseLink = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    component: Component = 'a',
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    color = 'loud',
    icon: Icon,
    iconPlacement = 'end',
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    underline = true,
    children,
    hideNewTabIcon = false,
    ...otherProps
  } = props;
  if (props.target === '_blank') {
    return /*#__PURE__*/_jsxs(Component, {
      ref: ref,
      ...otherProps,
      children: [Icon && (iconPlacement === 'left' || iconPlacement === 'start') && /*#__PURE__*/_jsx(IconAlignment, {
        iconPlacement: iconPlacement,
        children: /*#__PURE__*/_jsx(Icon, {
          fill: "currentColor"
        })
      }), /*#__PURE__*/_jsx("span", {
        children: children
      }), /*#__PURE__*/_jsx(Hidden, {
        screen: true,
        as: "span",
        children: ", opens in a new tab"
      }), !hideNewTabIcon && /*#__PURE__*/_jsx(IconAlignment, {
        iconPlacement: "right",
        children: /*#__PURE__*/_jsx(ExternalLinkSystemIcon, {})
      })]
    });
  }
  return Icon ? /*#__PURE__*/_jsxs(Component, {
    ref: ref,
    ...otherProps,
    children: [(iconPlacement === 'left' || iconPlacement === 'start') && /*#__PURE__*/_jsx(IconAlignment, {
      iconPlacement: iconPlacement,
      children: /*#__PURE__*/_jsx(Icon, {
        fill: "currentColor"
      })
    }), /*#__PURE__*/_jsx("span", {
      children: children
    }), (iconPlacement === 'right' || iconPlacement === 'end') && /*#__PURE__*/_jsx(IconAlignment, {
      iconPlacement: iconPlacement,
      children: /*#__PURE__*/_jsx(Icon, {
        fill: "currentColor"
      })
    })]
  }) : /*#__PURE__*/_jsx(Component, {
    ref: ref,
    ...otherProps,
    children: children
  });
});
const loudLink = css`
  color: var(--themeColorFgLink, ${linkLoud});

  &:hover {
    color: var(--themeColorFgLinkHover, ${linkLoud});
  }

  &:focus-visible {
    color: var(--themeColorFgLinkActive, ${linkLoud});
  }
`;
const mutedLink = css`
  color: currentColor;

  &:hover {
    color: var(--themeColorFgLinkHover, currentColor);
  }

  &:focus-visible {
    color: var(--themeColorFgLinkActive, currentColor);
  }
`;
const destructiveLink = css`
  color: var(--themeColorFgError, ${linkDestructive});
`;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const Link = styled(BaseLink).attrs(_ref3 => {
  let {
    theme,
    ...props
  } = _ref3;
  return {
    'data-mesh-component': 'LINK',
    color: 'loud',
    iconPlacement: 'end',
    underline: true,
    ...props
  };
})`
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  text-decoration: underline;
  text-underline-offset: 0.125em; /* 2px at 16px */
  text-decoration-thickness: min(0.125em, 4px); /* 2px at 16px */

  /* Handle long links (like urls) */
  /* These are technically the same, but use both */
  overflow-wrap: break-word;
  word-wrap: break-word;
  -ms-word-break: break-all;

  /* This is the dangerous one in WebKit, as it breaks things wherever */
  word-break: break-all;

  /* Instead use this non-standard one: */
  word-break: break-word;

  /* Subtly more weight for links, defaults to 400 font weight if 500 is not available for a brand */
  font-weight: ${token('theme.typography.link.fontWeight', 500)};

  /* By default, use the loudLink */
  ${_ref4 => {
  let {
    color
  } = _ref4;
  return !validColorValues.includes(color) && loudLink;
}};

  /* Otherwise we set the color to one of these three */
  ${_ref5 => {
  let {
    color
  } = _ref5;
  return color === 'loud' && loudLink;
}};
  ${_ref6 => {
  let {
    color
  } = _ref6;
  return color === 'dark' && mutedLink;
}};
  ${_ref7 => {
  let {
    color
  } = _ref7;
  return color === 'light' && mutedLink;
}};
  ${_ref8 => {
  let {
    color
  } = _ref8;
  return color === 'destructive' && destructiveLink;
}};

  ${_ref9 => {
  let {
    underline
  } = _ref9;
  return !underline && `text-decoration: none;`;
}}

  &:focus-visible {
    ${standardFocusStyleDeclarations};
  }
`;
Link.displayName = 'Link';
export default Link;